import {
    Filter,
    Filters,
    PaginatedTanstackQueryParams,
    Pagination,
    RouteMeta,
    Sort,
    Sorts,
} from '@/lib/types/v1'
import { useRouteQuery } from '@vueuse/router'
import { computed, ComputedRef, Reactive, reactive, Ref, watchEffect } from 'vue'
import {
    LocationQuery,
    RouteParamsGeneric,
    RouteRecordNameGeneric,
    useRoute as useBaseRoute,
    useRouter,
} from 'vue-router'
import { isArray } from '@/lib/utils'

export const useVueRoute = () => {
    return useBaseRoute()
}

export const assetParamKey = 'assetUuid'
export const articleParamKey = 'articleUuid'
export const collectorParamKey = 'collectorId'
export const bankFeedParamKey = 'bankFeedUuid'
export const bankFeedSourceParamKey = 'bankFeedSourceId'
export const categoryParamKey = 'categoryUuid'
export const cloudflareAccountParamKey = 'cloudflareAccountUuid'
export const cloudflareServerParamKey = 'cloudflareServerUuid'
export const cloudflareZoneParamKey = 'cloudflareZoneUuid'
export const cloudflareDnsRecordParamKey = 'cloudflareDnsRecordUuid'
export const companyParamKey = 'companyUuid'
export const contactParamKey = 'contactUuid'
export const companyLocationParamKey = 'locationUuid'
export const csParamKey = 'csUuid'
export const csUsageParamKey = 'csUsageUuid'
export const customerSiteTypeParamKey = 'customerSiteTypeId'
export const datumParamKey = 'datumId'
export const datumDimensionParamKey = 'datumDimensionId'
export const documentTemplateParamKey = 'documentTemplateId'
export const formParamKey = 'formUuid'
export const invoiceParamKey = 'invoiceUuid'
export const invoiceItemParamKey = 'invoiceItemUuid'
export const ipParamKey = 'ipId'
export const logParamKey = 'logId'
export const paymentParamKey = 'paymentUuid'
export const paymentItemParamKey = 'paymentItemUuid'
export const productParamKey = 'productUuid'
export const rateParamKey = 'rateUuid'
export const receiptParamKey = 'receiptUuid'
export const regexParamKey = 'regexUuid'
export const resourceParamKey = 'resourceId'
export const roleParamKey = 'roleId'
export const secretParamKey = 'secretUuid'
export const staffParamKey = 'staffUuid'
export const tariffParamKey = 'tariffUuid'
export const ticketParamKey = 'ticketId'
export const ticketTypeParamKey = 'ticketTypeUuid'
export const ticketPriorityParamKey = 'ticketPriorityUuid'
export const ticketDepartmentParamKey = 'ticketDepartmentUuid'
export const ticketTemplateParamKey = 'ticketTemplateUuid'
export const radiusUserParamKey = 'radiusUserUuid'
export const tenantParamKey = 'tenantUuid'
export const vocusMobileParamKey = 'vocusMobileUuid'
export const vocusMobileServerParamKey = 'vocusMobileServerUuid'

export enum RouteQueryPrefix {
    Articles = 'articles',
    ArticleCategories = 'article-categories',
    ArticleListTags = 'article-list-tags',
    ArticleTags = 'article-tags',
    BankFeeds = 'bank-feeds',
    BankFeedHistory = 'bank-feed-history',
    BankFeedSources = 'bank-feed-sources',
    Categories = 'categories',
    Collectors = 'collectors',
    Companies = 'companies',
    CompanyAssets = 'company-assets',
    CompanyContacts = 'company-contacts',
    CompanyLocations = 'company-locations',
    CompanyServices = 'company-services',
    CompanyServiceUsage = 'company-service-usage',
    CompanyStatement = 'company-statement',
    Countries = 'countries',
    CustomerSiteTypes = 'customer-site-types',
    Customers = 'customers',
    CustomerTypes = 'customer-types',
    CloudflareAccounts = 'cloudflare-accounts',
    CloudflareZones = 'cloudflare-zones',
    CloudflareZoneRecords = 'cloudflare-zone-records',
    CpanelAccounts = 'cpanel-accounts',
    CpanelEmailAccounts = 'cpanel-email-accounts',
    CpanelAddonDomains = 'cpanel-addon-domains',
    Datums = 'datums',
    DatumDimensions = 'datum-dimensions',
    DocumentTemplates = 'document-templates',
    ExpiringDomains = 'expiring-domains',
    EmailTemplates = 'email-templates',
    BillingExcluded = 'billing-excluded',
    FinanceBankFeedHistory = 'finance-bank-feed-history',
    FinanceCCPayments = 'finance-cc-payments',
    FinanceExpiredCards = 'finance-expired-cards',
    FinanceReceipts = 'finance-receipts',
    FinanceOrphanedInvoiceItems = 'finance-orphaned-invoice-items',
    Forms = 'forms',
    FNN = 'fnn',
    Groups = 'groups',
    Invoices = 'invoices',
    InvoiceItems = 'invoice-items',
    IPLists = 'ip-lists',
    Logs = 'logs',
    Links = 'links',
    Notes = 'notes',
    Payments = 'payments',
    PaymentItems = 'payment-items',
    Permissions = 'permissions',
    Products = 'products',
    Rates = 'rates',
    Receivables = 'receivables',
    Regexes = 'regexes',
    Regions = 'regions',
    Roles = 'roles',
    RSS = 'rss',
    Secrets = 'secrets',
    SentEmails = 'sent-emails',
    Services = 'services',
    ServiceGroups = 'service-groups',
    Staffs = 'staffs',
    SMS = 'sms',
    Tariffs = 'tariffs',
    TariffItems = 'tariff-items',
    Tickets = 'tickets',
    TicketTypes = 'ticket-types',
    TicketTemplates = 'ticket-templates',
    TicketPriorities = 'ticket-priorities',
    TicketResources = 'ticket-resources',
    TicketDepartments = 'ticket-departments',
    Tenants = 'tenants',
    RadiusUsers = 'radius-users',
    RadiusAccountings = 'radius-accountings',
    RadiusLogins = 'radius-logins',
    RadiusOnlineUsers = 'radius-online-users',
    VocusMobiles = 'vocus-mobiles',
    VocusMobileUsages = 'vocus-mobile-usages',
    VocusNBNServices = 'vocus-nbn-services',
    VocusNBNInvoices = 'vocus-nbn-invoices',
}

export type Route = {
    q: Ref<string>
    filters: Ref<Filters>
    groupBy: Ref<string>
    view: Ref<'flat' | 'tree'>
    sort: Ref<Sorts>
    pageIndex: Ref<number>
    limit: Ref<number>
    params: ComputedRef<RouteParamsGeneric>
    query: ComputedRef<LocationQuery>
    name: ComputedRef<RouteRecordNameGeneric>
    meta: ComputedRef<RouteMeta>
    bankFeedSourceId: ComputedRef<string>
    collectorId: ComputedRef<string>
    companyUuid: ComputedRef<string>
    companyContactUuid: ComputedRef<string>
    companyAssetUuid: ComputedRef<string>
    companyServiceUuid: ComputedRef<string>
    staffUuid: ComputedRef<string>
    ticketUuid: ComputedRef<string>
    addFilter: (filter: Filter) => Promise<void>
    addSortItem: (sort: Sort) => Promise<void>
    removeSortItem: (column: string) => Promise<void>
    setPagination: (item: Pagination) => void
    setLimit: (newLimit: number) => void
    setPageIndex: (newPageIndex: number) => void
    companyServiceUsageUuid: ComputedRef<string>
    documentTemplateId: ComputedRef<string>
    formUuid: ComputedRef<string>
    invoiceUuid: ComputedRef<string>
    cloudflareAccountUuid: ComputedRef<string>
    cloudflareServerUuid: ComputedRef<string>
    cloudflareZoneUuid: ComputedRef<string>
    cloudflareDnsRecordUuid: ComputedRef<string>
    paginatedTanstackQueryParams: Reactive<PaginatedTanstackQueryParams>
    rateUuid: ComputedRef<string>
}

export const useRoute = (prefix?: RouteQueryPrefix, defaultLimit = 100): Route => {
    const route = useBaseRoute()
    const router = useRouter()

    const paginatedTanstackQueryParams = reactive<PaginatedTanstackQueryParams>({
        enabled: false,
    })
    const qKey = prefix ? `${prefix}-q` : 'q'
    const groupByKey = prefix ? `${prefix}-group-by` : 'group-by'
    const viewKey = prefix ? `${prefix}-view` : 'view'
    const filterKey = prefix ? `${prefix}-filter` : 'filter'
    const sortKey = prefix ? `${prefix}-sort` : 'sort'
    const pageKey = prefix ? `${prefix}-page` : 'page'
    const limitKey = prefix ? `${prefix}-limit` : 'limit'
    const q = useRouteQuery(qKey, '')

    const groupBy = useRouteQuery(groupByKey)
    const view = useRouteQuery(viewKey, 'flat')
    const filters = useRouteQuery(filterKey, '', {
        mode: 'push',
        transform(val) {
            if (!val) return []

            try {
                return JSON.parse(val)
            } catch (e) {
                return []
            }
        },
    })

    const sort = useRouteQuery(sortKey, '', {
        transform(val) {
            if (!val) return []

            return JSON.parse(val)
        },
    })

    const pageIndex = useRouteQuery(pageKey, '1', {
        transform(val) {
            return parseInt(val)
        },
    })

    const limit = useRouteQuery(limitKey, defaultLimit, {
        transform(val: string | number) {
            if (typeof val === 'string') {
                return parseInt(val)
            }

            return val
        },
    })

    const addSortItem = async (newSort: Sort) => {
        let temp: null

        try {
            temp = JSON.parse(route.query[sortKey] as string)
        } catch (e) {
            temp = null
        }

        if (isArray(temp)) {
            const filter = temp.find((filter: Sort) => filter.column === newSort.column)

            if (!filter) {
                temp.push(newSort)
            } else {
                temp.splice(temp.indexOf(filter), 1, newSort)
            }
        } else {
            temp = [newSort]
        }

        await router.replace({
            query: {
                ...route.query,
                [sortKey]: JSON.stringify(temp),
            },
        })

        //sort.value = JSON.stringify(temp)
    }

    const removeSortItem = async (column: string) => {
        let temp

        try {
            temp = JSON.parse(route.query[sortKey] as string)
        } catch (e) {
            temp = []
        }

        const index = temp.findIndex((item: Sort) => item.column === column)

        if (index > -1) {
            temp.splice(index, 1)
        }

        await router.replace({
            query: {
                ...route.query,
                [sortKey]: JSON.stringify(temp),
            },
        })

        //sort.value = JSON.stringify(temp)
    }

    const addFilter = async (newFilter: Filter) => {
        let temp

        try {
            temp = JSON.parse(route.query[filterKey] as string)
        } catch (e) {
            temp = null
        }

        if (isArray(temp)) {
            const filterIndex = temp.findIndex(
                (filter: Filter) => filter.column === newFilter.column
            )

            if (filterIndex === -1) {
                temp.push(newFilter)
            } else {
                temp.splice(filterIndex, 1, newFilter)
            }
        } else {
            temp = [newFilter]
        }

        await router.replace({
            query: {
                ...route.query,
                [filterKey]: JSON.stringify(temp),
            },
        })
        //filters.value = JSON.stringify(temp)
    }

    const setPagination = (item: Pagination) => {
        pageIndex.value = item.pageIndex + 1
        limit.value = item.pageSize
    }

    const setLimit = async (newLimit: number) => {
        await router.replace({
            query: {
                ...route.query,
                [limitKey]: newLimit,
            },
        })
        //limit.value = newLimit
    }

    const setPageIndex = (newPageIndex: number) => {
        pageIndex.value = newPageIndex + 1
    }

    const params = computed(() => route.params)
    const query = computed(() => route.query)
    const name = computed(() => route.name)
    const meta = computed(() => route.meta as RouteMeta)
    const bankFeedSourceId = computed(() => route.params[bankFeedSourceParamKey] as string)
    const cloudflareAccountUuid = computed(() => route.params[cloudflareAccountParamKey] as string)
    const cloudflareServerUuid = computed(() => route.params[cloudflareServerParamKey] as string)
    const cloudflareZoneUuid = computed(() => route.params[cloudflareZoneParamKey] as string)
    const cloudflareDnsRecordUuid = computed(
        () => route.params[cloudflareDnsRecordParamKey] as string
    )
    const collectorId = computed(() => route.params[collectorParamKey] as string)
    const companyUuid = computed(() => route.params[companyParamKey] as string)
    const companyAssetUuid = computed(() => route.params[assetParamKey] as string)
    const companyServiceUuid = computed(() => route.params[csParamKey] as string)
    const companyContactUuid = computed(() => route.params[contactParamKey] as string)
    const companyLocationUuid = computed(() => route.params[companyLocationParamKey] as string)
    const customerSiteTypeId = computed(() => route.params[customerSiteTypeParamKey] as string)
    const documentTemplateId = computed(() => route.params[documentTemplateParamKey] as string)
    const formUuid = computed(() => route.params[formParamKey] as string)
    const invoiceUuid = computed(() => route.params[invoiceParamKey] as string)
    const invoiceItemUuid = computed(() => route.params[invoiceItemParamKey] as string)
    const logId = computed(() =>
        route.params[logParamKey] ? parseInt(route.params[logParamKey] as string) : 0
    )
    const paymentUuid = computed(() => route.params[paymentParamKey] as string)
    const paymentItemUuid = computed(() => route.params[paymentItemParamKey] as string)
    const productUuid = computed(() => route.params[productParamKey] as string)
    const rateUuid = computed(() => route.params[rateParamKey] as string)
    const secretUuid = computed(() => route.params[secretParamKey] as string)
    const staffUuid = computed(() => route.params[staffParamKey] as string)
    const tenantUuid = computed(() => route.params[tenantParamKey] as string)
    const ticketUuid = computed(() => route.params[ticketParamKey] as string)
    const articleUuid = computed(() => route.params[articleParamKey] as string)
    const bankFeedUuid = computed(() => route.params[bankFeedParamKey] as string)
    const categoryUuid = computed(() => route.params[categoryParamKey] as string)
    const regexUuid = computed(() => route.params[regexParamKey] as string)
    const tariffUuid = computed(() => route.params[tariffParamKey] as string)
    const ticketTypeUuid = computed(() => route.params[ticketTypeParamKey] as string)
    const ticketPriorityUuid = computed(() => route.params[ticketPriorityParamKey] as string)
    const ticketDepartmentUuid = computed(() => route.params[ticketDepartmentParamKey] as string)
    const ticketTemplateUuid = computed(() => route.params[ticketTemplateParamKey] as string)
    const radiusUserUuid = computed(() => route.params[radiusUserParamKey] as string)
    const vocusMobileServerUuid = computed(() => route.params[vocusMobileServerParamKey] as string)
    const vocusMobileUuid = computed(() => route.params[vocusMobileParamKey] as string)
    const ipId = computed(() => route.params[ipParamKey] as string)
    const datumId = computed(() => route.params[datumParamKey] as string)
    const datumDimensionId = computed(() => route.params[datumDimensionParamKey] as string)

    watchEffect(() => {
        paginatedTanstackQueryParams.q = q.value
        paginatedTanstackQueryParams.groupBy = groupBy.value
        paginatedTanstackQueryParams.view = view.value
        paginatedTanstackQueryParams.filter = filters.value
        paginatedTanstackQueryParams.sort = sort.value
        paginatedTanstackQueryParams.page = pageIndex.value
        paginatedTanstackQueryParams.limit = limit.value
        paginatedTanstackQueryParams.companyUuid = companyUuid.value
    })

    return {
        q,
        filters,
        groupBy,
        sort,
        pageIndex,
        limit,
        params,
        query,
        name,
        meta,
        cloudflareAccountUuid,
        cloudflareServerUuid,
        cloudflareZoneUuid,
        cloudflareDnsRecordUuid,
        companyUuid,
        companyServiceUuid,
        companyAssetUuid,
        companyContactUuid,
        companyLocationUuid,
        formUuid,
        invoiceUuid,
        invoiceItemUuid,
        logId,
        paymentUuid,
        paymentItemUuid,
        productUuid,
        rateUuid,
        secretUuid,
        addSortItem,
        removeSortItem,
        addFilter,
        setPagination,
        setLimit,
        setPageIndex,
        view,
        staffUuid,
        ticketUuid,
        articleUuid,
        tenantUuid,
        bankFeedUuid,
        categoryUuid,
        ticketTypeUuid,
        ticketPriorityUuid,
        ticketDepartmentUuid,
        vocusMobileServerUuid,
        vocusMobileUuid,
        radiusUserUuid,
        regexUuid,
        tariffUuid,
        ticketTemplateUuid,
        collectorId,
        documentTemplateId,
        bankFeedSourceId,
        customerSiteTypeId,
        ipId,
        datumId,
        datumDimensionId,
        paginatedTanstackQueryParams,
    }
}

export const useAppRoute = (prefix?: RouteQueryPrefix, defaultLimit = 100): Route =>
    useRoute(prefix, defaultLimit)
